'use client'

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Article } from './site-config'

export type CartItem = {
  article: Article
  quantity: number
}

type CartState = {
  items: CartItem[]
  isOpen: boolean
  addItem: (article: Article, quantity?: number) => void
  removeItem: (articleId: string) => void
  updateQuantity: (articleId: string, quantity: number) => void
  clear: () => void
  openCart: () => void
  closeCart: () => void
  toggleCart: () => void
  getTotalItems: () => number
  getTotalPrice: () => number
}

export const useCart = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      isOpen: false,
      addItem: (article, quantity = 1) => {
        const items = get().items
        const existing = items.find(i => i.article.id === article.id)
        if (existing) {
          set({
            items: items.map(i =>
              i.article.id === article.id
                ? { ...i, quantity: i.quantity + quantity }
                : i
            ),
            isOpen: true,
          })
        } else {
          set({ items: [...items, { article, quantity }], isOpen: true })
        }
      },
      removeItem: (articleId) => {
        set({ items: get().items.filter(i => i.article.id !== articleId) })
      },
      updateQuantity: (articleId, quantity) => {
        if (quantity <= 0) {
          get().removeItem(articleId)
          return
        }
        set({
          items: get().items.map(i =>
            i.article.id === articleId ? { ...i, quantity } : i
          ),
        })
      },
      clear: () => set({ items: [] }),
      openCart: () => set({ isOpen: true }),
      closeCart: () => set({ isOpen: false }),
      toggleCart: () => set(s => ({ isOpen: !s.isOpen })),
      getTotalItems: () => get().items.reduce((sum, i) => sum + i.quantity, 0),
      getTotalPrice: () =>
        get().items.reduce((sum, i) => sum + i.article.price * i.quantity, 0),
    }),
    {
      name: 'sadiya-cart',
      partialize: (s) => ({ items: s.items }) as CartState,
    }
  )
)
